// The multi-SKU map for one channel: which Portal SKUs sit behind each
// listing the channel sells.
//
// Shared by every multi-SKU family (Shopify stores, Amazon
// marketplaces), because the question is identical on both — the code
// branches on the channel's family nowhere in here.
//
// The panel exists because two states are invisible otherwise, and both
// stop stock moving:
//   * a listing with NOTHING behind it (nothing to sell from)
//   * a listing pointing at a Portal SKU that does not exist
// Each is called out rather than left to be inferred from a quantity
// that never changes.

import { useCallback, useEffect, useState } from "react";
import { Loader2, Link2, Unlink, Search, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import type { SkuMapListing } from "./types";

interface Props {
    channelCode: string;
    // Bumped by the parent after a catalogue sync so the panel reloads.
    reloadToken?: number;
}

const SkuMapPanel = ({ channelCode, reloadToken = 0 }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const base = `/admin/integrations/${encodeURIComponent(channelCode)}/sku-map`;

    const [listings, setListings] = useState<SkuMapListing[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [error, setError] = useState<string | null>(null);

    // Add-link form.
    const [linkExternal, setLinkExternal] = useState("");
    const [linkSku, setLinkSku] = useState("");
    const [linking, setLinking] = useState(false);

    const load = useCallback(async (term: string) => {
        setLoading(true);
        setError(null);
        try {
            const url = term ? `${base}?search=${encodeURIComponent(term)}` : base;
            const res = await apiFetch(url);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            setListings(Array.isArray(json?.listings) ? json.listings : []);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [base]);

    useEffect(() => { load(""); }, [load, reloadToken]);

    const submitLink = async (e: React.FormEvent) => {
        e.preventDefault();
        if (readOnly) return;
        const sku = linkSku.trim();
        const externalSku = linkExternal.trim();
        if (!sku || !externalSku) return;
        setLinking(true);
        setError(null);
        try {
            const res = await apiFetch(base, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sku, externalSku }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            toast.success(t("sku_map_linked", { sku, external: externalSku }));
            // The BE warns rather than refuses when the Portal SKU does
            // not exist yet — the product may be created moments later.
            if (json?.warning) toast.warn(json.warning);
            setLinkSku("");
            await load(search);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLinking(false);
        }
    };

    const unlink = async (sku: string) => {
        if (readOnly) return;
        setError(null);
        try {
            const res = await apiFetch(`${base}/${encodeURIComponent(sku)}`, { method: "DELETE" });
            if (!res.ok) {
                const json = await res.json().catch(() => ({}));
                throw new Error(json?.message ?? `HTTP ${res.status}`);
            }
            await load(search);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    };

    const empty = listings !== null && listings.length === 0;
    const unbacked = (listings ?? []).filter((l) => l.portalSkus.every((p) => !p.known)).length;

    return (
        <details className="admin-integrations-vendors-details">
            <summary className="admin-integrations-vendors-summary">
                <span>{t("sku_map_heading")}</span>
                <span className="admin-integrations-vendors-count">
                    {listings?.length ?? "—"}
                </span>
            </summary>
            <div className="admin-integrations-vendors-body">
                <p className="admin-integrations-vendors-hint">{t("sku_map_hint")}</p>

                {unbacked > 0 && (
                    <p className="admin-integrations-field-error">
                        <AlertTriangle size={14} /> {t("sku_map_unbacked", { n: String(unbacked) })}
                    </p>
                )}

                <form
                    className="admin-integrations-form-row"
                    onSubmit={(e) => { e.preventDefault(); load(search); }}
                >
                    <input
                        type="search"
                        className="admin-integrations-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("sku_map_search_placeholder")}
                        aria-label={t("sku_map_search_placeholder")}
                    />
                    <button type="submit" className="btn btn-sm" disabled={loading}>
                        {loading
                            ? <Loader2 size={14} className="admin-integrations-spinner" />
                            : <><Search size={14} /> {t("search")}</>}
                    </button>
                </form>

                {error && <p className="admin-integrations-field-error">{error}</p>}

                {empty ? (
                    <p className="admin-integrations-vendors-empty">{t("sku_map_empty")}</p>
                ) : (
                    <div className="admin-integrations-vendors-list">
                        {(listings ?? []).map((l) => (
                            <div key={l.externalSku} className="admin-integrations-sku-map-row">
                                <div className="admin-integrations-sku-map-listing">
                                    <strong>{l.externalSku}</strong>
                                    {l.asin && <span className="admin-integrations-secret-rotated"> · {l.asin}</span>}
                                    {l.title && (
                                        <span className="admin-integrations-card-summary-sub"> {l.title}</span>
                                    )}
                                    {!l.tracked && (
                                        <span className="admin-integrations-pill"> {t("sku_map_untracked")}</span>
                                    )}
                                </div>
                                <div className="admin-integrations-sku-map-skus">
                                    {l.portalSkus.map((p) => (
                                        <span
                                            key={p.sku}
                                            className={`admin-integrations-pill${p.known ? "" : " admin-integrations-pill--dry-run"}`}
                                            title={p.known ? undefined : t("sku_map_unknown_sku")}
                                        >
                                            {p.sku}
                                            {p.isManual && " ✎"}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-ghost"
                                                onClick={() => unlink(p.sku)}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                aria-label={t("sku_map_unlink")}
                                                title={readOnly ? t("read_only_mode") : t("sku_map_unlink")}
                                            >
                                                <Unlink size={12} />
                                            </button>
                                        </span>
                                    ))}
                                    {/* Several SKUs behind one listing is the
                                        point of the feature, so say what it
                                        means rather than leaving it implied. */}
                                    {l.portalSkus.length > 1 && (
                                        <span className="admin-integrations-secret-rotated">
                                            {t("sku_map_summed", { n: String(l.portalSkus.length) })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <form className="admin-integrations-add-vendor" onSubmit={submitLink}>
                    <p className="admin-integrations-vendors-hint">{t("sku_map_link_hint")}</p>
                    <div className="admin-integrations-form-row">
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={linkExternal}
                            onChange={(e) => setLinkExternal(e.target.value)}
                            placeholder={t("sku_map_external_placeholder")}
                            aria-label={t("sku_map_external_placeholder")}
                            disabled={linking || readOnly}
                        />
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={linkSku}
                            onChange={(e) => setLinkSku(e.target.value)}
                            placeholder={t("sku_map_portal_placeholder")}
                            aria-label={t("sku_map_portal_placeholder")}
                            disabled={linking || readOnly}
                        />
                        <button
                            type="submit"
                            className="btn btn-sm btn-primary"
                            disabled={linking || readOnly || !linkSku.trim() || !linkExternal.trim()}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {linking
                                ? <Loader2 size={14} className="admin-integrations-spinner" />
                                : <><Link2 size={14} /> {t("sku_map_link")}</>}
                        </button>
                    </div>
                </form>
            </div>
        </details>
    );
};

export default SkuMapPanel;
